import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Send, AlertTriangle, Paperclip, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errMsg } from "@/lib/api";
import { EMAIL_RX, lastRecipients } from "@/lib/calendar";

const COPY = {
  invito: { title: "Invia invito", desc: "Il destinatario riceverà un'email con i dettagli e il file .ics da aggiungere al calendario.", btn: "Invia invito" },
  aggiornamento: { title: "Invia aggiornamento", desc: "L'evento già presente nel calendario dei destinatari verrà aggiornato con i nuovi dettagli.", btn: "Invia aggiornamento" },
  annullamento: { title: "Invia annullamento", desc: "I destinatari riceveranno la cancellazione e l'evento verrà rimosso dal loro calendario.", btn: "Invia annullamento" },
};

export const SendInviteDialog = ({ open, onOpenChange, kind = "invito", appt, onSent }) => {
  const [to, setTo] = useState([]);
  const [extra, setExtra] = useState("");
  const [ccSoci, setCcSoci] = useState(true);
  const [reminder, setReminder] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !appt) return;
    setTo(kind === "invito" ? (appt.client_email ? [appt.client_email] : []) : lastRecipients(appt));
    setExtra(""); setError(""); setCcSoci(appt.partecipanti?.length > 0); setReminder(!!appt.promemoria_cliente);
  }, [open, appt, kind]);

  const add = () => {
    const parts = extra.split(/[,;\s]+/).map((s) => s.trim().toLowerCase()).filter(Boolean);
    const bad = parts.filter((p) => !EMAIL_RX.test(p));
    if (bad.length) return toast.error(`Email non valida: ${bad.join(", ")}`);
    setTo((p) => [...new Set([...p, ...parts])]);
    setExtra("");
  };

  const send = async () => {
    if (extra.trim()) return toast.error("Premi «Aggiungi» per confermare l'indirizzo inserito");
    if (!to.length) return setError("Aggiungi almeno un destinatario.");
    setSending(true); setError("");
    try {
      const r = await api.post(`/appointments/${appt.id}/send`, { kind, to, cc_soci: ccSoci, ...(kind !== "annullamento" && { promemoria_cliente: reminder }) });
      toast.success(kind === "annullamento" ? "Annullamento inviato" : kind === "aggiornamento" ? "Aggiornamento inviato" : "Invito inviato");
      onOpenChange(false);
      onSent?.(r.data);
    } catch (e) {
      const m = errMsg(e, "Invio non riuscito");
      setError(m); toast.error(m);
    } finally { setSending(false); }
  };

  if (!appt) return null;
  const c = COPY[kind];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-2xl" data-testid="send-invite-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black text-[#0A1128]">{c.title}</DialogTitle>
          <DialogDescription>{c.desc}</DialogDescription>
        </DialogHeader>
        {!appt.client_email && kind === "invito" && (
          <div className="flex gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="invite-no-client-email">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            Il cliente «{appt.client_nome}» non ha un indirizzo email in anagrafica. Aggiungi manualmente almeno un destinatario oppure completa l'anagrafica.
          </div>
        )}
        <div className="space-y-2">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Destinatari</div>
          <div className="flex flex-wrap gap-2 min-h-[36px]" data-testid="invite-recipients">
            {to.map((e) => (
              <span key={e} className="inline-flex items-center gap-1.5 rounded-full bg-[#A6DAFC] text-[#0A1128] text-sm font-semibold pl-3 pr-1.5 py-1" data-testid={`invite-recipient-${e}`}>
                {e}
                <button onClick={() => setTo((p) => p.filter((x) => x !== e))} className="rounded-full p-0.5 hover:bg-[#A6DAFC]" data-testid={`invite-remove-${e}`}><X className="h-3.5 w-3.5" /></button>
              </span>
            ))}
            {!to.length && <span className="text-sm text-slate-400">Nessun destinatario</span>}
          </div>
          <div className="flex gap-2">
            <Input value={extra} onChange={(e) => setExtra(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())}
              placeholder="Aggiungi email (separa con virgola)" className="rounded-xl" data-testid="invite-extra-input" />
            <Button type="button" variant="outline" onClick={add} className="rounded-full font-bold" data-testid="invite-add-button">Aggiungi</Button>
          </div>
        </div>
        {appt.partecipanti_nomi?.length > 0 && (
          <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 cursor-pointer">
            <Checkbox checked={ccSoci} onCheckedChange={(v) => setCcSoci(!!v)} data-testid="invite-cc-soci" />
            Metti in CC i soci partecipanti ({appt.partecipanti_nomi.join(", ")})
          </label>
        )}
        {kind !== "annullamento" && (
          <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 cursor-pointer">
            <Checkbox checked={reminder} onCheckedChange={(v) => setReminder(!!v)} data-testid="invite-promemoria-cliente" />
            Promemoria al cliente il giorno prima (alle 10:00)
          </label>
        )}
        <div className="flex items-center gap-2 text-xs text-slate-500"><Paperclip className="h-3.5 w-3.5" />Allegato: {kind === "annullamento" ? "annullamento.ics" : "invito.ics"} (fuso orario Europe/Rome)</div>
        {error && <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm font-semibold text-[#C8102E]" data-testid="invite-error">{error}</div>}
        <DialogFooter>
          <Button variant="ghost" className="rounded-full" onClick={() => onOpenChange(false)} data-testid="invite-cancel">Annulla</Button>
          <Button onClick={send} disabled={sending} className="rounded-full bg-[#E8173A] hover:bg-[#C8102E] px-6 font-bold" data-testid="invite-send-button">
            {sending ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Send className="h-4 w-4 mr-1.5" />}{c.btn}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
